import { useEffect, useState } from "react";
import classNames from "classnames";

import { emojiFlagToIsoCode } from "./emojiToIso";
import styles from "./styles.module.scss";

interface Props {
  /** The flag emoji returned by the backend, e.g. "🇸🇦". */
  emoji: string;
  /** Country name, used for the image alt / aria label. */
  countryName?: string;
  /** Rendered width in px (height follows the 4:3 flag ratio). */
  size?: number;
  className?: string;
}

const FLAGS_BASE_URL = "https://waffy-files.s3.eu-west-1.amazonaws.com/flags";

const getFlagSrc = (isoCode: string): string =>
  `${FLAGS_BASE_URL}/${isoCode}.svg`;

/**
 * Renders a country's flag as an SVG hosted on the Waffy files bucket so it looks identical on
 * every OS (Windows Chrome does not render flag emoji). Falls back to the raw emoji when the
 * flag can't be resolved to an SVG or the asset fails to load.
 */
const CountryFlag = ({ emoji, countryName, size = 24, className }: Props) => {
  const isoCode = emojiFlagToIsoCode(emoji);
  const [hasFailed, setHasFailed] = useState(false);

  // Reset the failure state when the flag changes (component is reused across selections).
  useEffect(() => {
    setHasFailed(false);
  }, [emoji]);

  if (!isoCode || hasFailed) {
    return (
      <span
        className={classNames(styles.flagEmoji, className)}
        style={{ fontSize: size }}
        role="img"
        aria-label={countryName}>
        {emoji}
      </span>
    );
  }

  return (
    <img
      className={classNames(styles.flag, className)}
      src={getFlagSrc(isoCode)}
      alt={countryName || isoCode}
      width={size}
      height={Math.round((size * 3) / 4)}
      draggable={false}
      onError={() => setHasFailed(true)}
    />
  );
};

export default CountryFlag;
